<template>
    <div class="flex flex-col items-start">
        <Card class="w-full max-w-2xl border border-surface-200" :pt="{ body: { class: 'p-4' } }">
            <template #content>
                <div class="flex items-center gap-2 mb-1">
                    <!-- MYAC-1302: the title is agent-written too (adopt / unadopt / withdraw
                         titles carry the catalogue's "ISO 14971 — Risk management"), so it
                         gets the same em-dash flattening as the body below. -->
                    <div class="font-semibold">{{ stripEmDashes(titleText) }}</div>
                    <!-- Append previews are model-extracted rows (AI Act Art. 50(2)).
                         A repair card carries the CUSTOMER's own words, so it is
                         explicitly NOT tagged — it is the one thing on the page that
                         is not model-authored.

                         `isTask` was too broad (review #550) and broke that same
                         carve-out: a REJECT card shows the user's own reason back to
                         them, and archive/re-approve show no authored content at all.
                         Tagging those claims the model wrote something it did not,
                         which is the label being wrong in the direction that matters
                         — Art. 50(2) is about identifying machine-authored content,
                         not about decorating every card an agent proposed. -->
                    <AiGeneratedTag v-if="isAppend || showsAiAuthoredContent" />
                </div>
                <div class="text-sm text-surface-600 mb-2">{{ stripEmDashes(messageText) }}</div>

                <!-- Register edit preview (MYAC-848): the exact rows that would change -->
                <div v-if="isEdit && previewRows.length"
                    class="mb-2 border border-surface-200 rounded overflow-auto max-h-64">
                    <table class="w-full text-xs">
                        <thead class="sticky top-0 bg-surface-100">
                            <tr>
                                <th v-for="col in previewColumns" :key="col.key"
                                    class="text-left px-2 py-1 font-medium whitespace-nowrap">
                                    {{ col.label }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, i) in previewRows" :key="i" class="border-t border-surface-100">
                                <td v-for="col in previewColumns" :key="col.key" class="px-2 py-1 align-top">
                                    {{ row[col.key] ?? '-' }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="hiddenRowCount > 0" class="text-xs text-surface-500 mb-2">
                    {{ ct('chat.action_approval.more_rows', { count: hiddenRowCount }, hiddenRowCount) }}
                </div>
                <div v-if="isEdit && proposal.duplicates?.length" class="text-xs text-amber-700 mb-2">
                    {{ ct('chat.action_approval.duplicates', { refs: proposal.duplicates.join(', ') }) }}
                </div>
                <div v-if="isEdit && proposal.not_found?.length" class="text-xs text-amber-700 mb-2">
                    {{ ct('chat.action_approval.not_found', { refs: proposal.not_found.join(', ') }) }}
                </div>

                <!--
                    MYAC-1127 — a board change, shown as what it does rather than
                    described. An edit card the user cannot read the before/after on
                    is a Yes/No on trust, and the whole point of the card is that it
                    is not.
                -->
                <div v-if="isTaskEdit && taskChanges.length"
                    class="mb-2 border border-surface-200 rounded overflow-auto max-h-64">
                    <table class="w-full text-xs">
                        <tbody>
                            <tr v-for="row in taskChanges" :key="row.field"
                                class="border-b border-surface-100 last:border-0">
                                <th class="text-left px-2 py-1 font-medium whitespace-nowrap align-top">
                                    {{ fieldLabel(row) }}
                                </th>
                                <td class="px-2 py-1 align-top text-surface-500 line-through">
                                    {{ row.before || '-' }}
                                </td>
                                <td class="px-2 py-1 align-top">{{ row.after }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!--
                    MYAC-1135 — the document half of a merged card. Rendered as a
                    list of operations and not as a before/after, because a list
                    field's value is the WHOLE list: showing that as a diff would
                    show the user everything they did not ask to change.

                    Drafted text gets its own block rather than a row, because it
                    IS the deliverable — approving without reading it is approving
                    nothing — so it is shown at length while the rest stay one line.
                -->
                <div v-if="isTaskDocuments" class="mb-2 flex flex-col gap-2">
                    <div v-for="(row, i) in taskDocumentChanges" :key="i"
                        class="border rounded p-2 text-xs"
                        :class="row.op === 'remove_document'
                            ? 'border-amber-200' : 'border-surface-200'">
                        <div class="font-medium">{{ documentChangeLabel(row) }}</div>
                        <!--
                            Per ROW, not per card (review, PR #574). One merged card
                            can carry several document rows and only one of them may
                            overwrite drafted text; a card-level warning either points
                            at nothing the user can identify, or implies every row
                            overwrites. `replaced` says which one, so it is what the
                            warning hangs on.
                        -->
                        <div v-if="row.replaced" class="text-amber-700 mt-1">
                            {{ ct('chat.action_approval.task_content_replaces') }}
                        </div>
                        <div v-if="documentChangeDetail(row.detail)"
                            class="mt-1 whitespace-pre-wrap overflow-auto max-h-64 text-surface-600">
                            {{ documentChangeDetail(row.detail) }}
                        </div>
                    </div>
                </div>

                <!-- The drafted text IS the deliverable here: approving without
                     reading it is approving nothing, so it is shown at length. -->
                <div v-if="isTaskContent" class="mb-2">
                    <div v-if="proposal.replaces_existing" class="text-xs text-amber-700 mb-1">
                        {{ ct('chat.action_approval.task_content_replaces') }}
                    </div>
                    <div
                        class="border border-surface-200 rounded p-2 text-xs whitespace-pre-wrap overflow-auto max-h-64">
                        {{ stripEmDashes(proposal.content) }}
                    </div>
                </div>

                <div v-if="isTaskCreate && taskFields.length"
                    class="mb-2 border border-surface-200 rounded overflow-auto max-h-64">
                    <table class="w-full text-xs">
                        <tbody>
                            <tr v-for="row in taskFields" :key="row.field"
                                class="border-b border-surface-100 last:border-0">
                                <th class="text-left px-2 py-1 font-medium whitespace-nowrap align-top">
                                    {{ fieldLabel(row) }}
                                </th>
                                <td class="px-2 py-1 align-top">{{ row.value }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!--
                    MYAC-1127 — WHAT is being added, not just that something is.
                    The first version of these cards showed the task's name and a
                    generic sentence, so "attach the audit templates" produced a
                    card the user could only approve on trust. A confirmation card
                    that does not show its own content is a Yes/No on nothing.
                -->
                <div v-if="isTaskAddDocument || isTaskAddAction" class="mb-2">
                    <div v-if="proposal.document" class="text-xs font-medium mb-1">
                        {{ proposal.document }}
                    </div>
                    <ul v-if="taskActions.length" class="text-xs list-disc ml-4 space-y-0.5">
                        <li v-for="(a, i) in taskActions" :key="i">{{ a }}</li>
                    </ul>
                    <div v-else-if="isTaskAddDocument" class="text-xs text-surface-500">
                        {{ ct('chat.action_approval.task_doc_no_actions') }}
                    </div>
                </div>

                <div v-if="isTaskRemoveDocument && proposal.document"
                    class="mb-2 text-xs border border-amber-200 rounded p-2">
                    {{ proposal.document }}
                </div>

                <!-- A rejection reason is the user's own words going on the
                     record, so it is shown back before it is written. -->
                <div v-if="isTaskReject && (proposal.reason || setAsideReasonKey)"
                    class="mb-2 text-xs border border-surface-200 rounded p-2 whitespace-pre-wrap">
                    <div v-if="setAsideReasonKey" class="font-semibold">{{ ct(setAsideReasonKey) }}</div>
                    <div v-if="proposal.reason">{{ proposal.reason }}</div>
                </div>

                <!-- No card says what it costs, nor that it is free: a price notice puts
                     people off the action the card is about (Aymeric, 2026-10-14,
                     MYAC-1582). The credit gate on a paid card's button stays; the only
                     line left here warns about what the action does. -->
                <div v-if="isTaskArchive" class="text-xs text-amber-700 mb-3 flex items-center gap-1">
                    <i class="pi pi-exclamation-triangle" />
                    {{ ct('chat.action_approval.task_archive_warning') }}
                </div>
                <div v-else-if="isDestructive" class="text-xs text-amber-700 mb-3 flex items-center gap-1">
                    <i class="pi pi-exclamation-triangle" />
                    {{ ct('chat.action_approval.unadopt_warning') }}
                </div>

                <!--
                    MYAC-1135 — a card another card has replaced. The chat reissues
                    the card each time a gesture merges onto the same task, so this
                    one's proposal_id is gone from the pending map: pressing its
                    buttons could only ever answer "expired". Seen live 2026-08-28
                    as a stack of identical-looking cards where only the last one
                    worked. The buttons go, and one line says where the live card
                    is — the body stays, because it is an honest record of what was
                    proposed at that point of the conversation.
                -->
                <div v-if="isSuperseded" class="text-xs text-surface-500 italic">
                    {{ ct('chat.action_approval.superseded') }}
                </div>
                <!-- MYAC-1269 — someone else settled this card in a shared conversation:
                     their name stands where the buttons were, so every reader sees who
                     committed the project to what. -->
                <div v-else-if="decided" class="text-xs flex items-center gap-1"
                    :class="decided.decision === 'approve' ? 'text-green-700' : 'text-surface-600'"
                    data-testid="action-proposal-decided">
                    <i :class="decided.decision === 'approve' ? 'pi pi-check-circle' : 'pi pi-times-circle'" aria-hidden="true" />
                    <i18n-t :keypath="decidedKey" tag="span" :locale="cardLocale ?? undefined">
                        <template #name><UserName :uid="decided.decidedBy" /></template>
                    </i18n-t>
                </div>
                <!-- MYAC-1269 — a reader of a shared conversation may not decide: the
                     buttons stay, disabled, with the reason on the wrapper (a disabled
                     PrimeVue button swallows its own tooltip). The backend refuses the
                     decision anyway (`read_only`); this says so before the click. -->
                <div v-else-if="readOnly" v-tooltip.top="ct('chat.shared.read_only_hint')" class="flex gap-2"
                    data-testid="action-proposal-read-only">
                    <Button :label="approveLabel" size="small" disabled />
                    <Button :label="declineLabel" size="small" severity="secondary" outlined disabled />
                </div>
                <div v-else class="flex gap-2">
                    <!-- $$$ rule (front CLAUDE.md): approving a repair raises a PAID
                         scoped re-assessment, so the trigger sits behind the credit
                         gate — the same per-requirement key the Referentials page
                         uses for the identical re-run (MYAC-1469: one key, both
                         tracks). Register cards keep their historic path: their cost is
                         gated by the backend check_action on the Assessment write. -->
                    <CreditGate v-if="startsAPaidRun" :action="repairCreditAction"
                        :count="reassessCount">
                        <Button :label="approveLabel" size="small" :loading="busy"
                            @click="decide('approve')" />
                    </CreditGate>
                    <Button v-else :label="approveLabel" size="small" :loading="busy"
                        @click="decide('approve')" />
                    <Button :label="declineLabel" size="small" severity="secondary"
                        outlined :disabled="busy" @click="decide('decline')" />
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { rejectionReasonKey } from '@/models/rejectionReason'
import { stripEmDashes } from '@/utils/typography'
import { ref, computed } from 'vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import CreditGate from '@/components/shared/CreditGate.vue'
import AiGeneratedTag from '@/components/shared/AiGeneratedTag.vue'
import UserName from '@/components/shared/users/UserName.vue'
import { useChatbotStore, type ChatDecision } from '@/stores/chatbot'
import { useCardLocale } from '@/composables/useCardLocale'
import type { ActionApprovalResponse } from '@/models/ChatbotEvent'
import { ASSESSMENT_CREDIT_ACTION } from '@/utils/assessmentViews'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'

const props = withDefaults(defineProps<{
    proposal: ActionApprovalResponse
    /** MYAC-1269 — the person reads this shared conversation and may not decide. */
    readOnly?: boolean
    /** MYAC-1269 — someone else already decided it: shown settled, with their name. */
    decided?: ChatDecision | null
}>(), { readOnly: false, decided: null })

const { ct, cardLocale } = useCardLocale(computed(() => props.proposal.language))
const decidedKey = computed(() => {
    const verdict = props.decided?.decision
    return `chat.action_approval.decided_${verdict === 'approve' ? 'approve' : verdict === 'decline' ? 'decline' : 'unknown'}`
})
const chatbotStore = useChatbotStore()

/**
 * Whether a LATER card replaced this one — true when any function response in
 * the transcript names this card's proposal_id as its `supersedes`. Chains
 * resolve naturally: A→B→C leaves only C unnamed, so only C keeps its buttons.
 */
const isSuperseded = computed(() => {
    const id = props.proposal.proposal_id
    if (!id) return false
    return (chatbotStore.events ?? []).some((event) =>
        event.content?.parts?.some((part) => {
            const response = part.functionResponse?.response as
                { supersedes?: string } | undefined
            return response?.supersedes === id
        }))
})
const busy = ref(false)

// Cap the rows rendered on the card; the full count is stated via more_rows.
const MAX_PREVIEW_ROWS = 30

const isEdit = computed(() =>
    props.proposal.action === 'propose_register_append' ||
    props.proposal.action === 'propose_register_delete')
const isAppend = computed(() => props.proposal.action === 'propose_register_append')
const isRepair = computed(() => props.proposal.action === 'propose_requirement_repair')
/** MYAC-1044 — a scoped re-assessment starts a paid run exactly as a repair does, so it
 *  is gated the same way; only the UNIT differs (a repair is always one requirement). */
const isReassess = computed(() => props.proposal.action === 'propose_reassessment')
/** R16 — an accepted suggestion's confirm label is « Add » (it joins the watch list);
 *  a dismissed one is « Dismiss ». See `APPROVE_KEY_BY_ACTION`. */
const isStandardWatch = computed(() =>
    props.proposal.action === 'propose_standard_disposition' &&
    props.proposal.decision === 'accepted')
/** The warning keys on the proposal's own `destructive` flag, not on the action:
 *  the next destructive card gets the line by setting the flag, without a new
 *  branch here. The verb still keys on the action (R16). */
const isDestructive = computed(() => props.proposal.destructive === true)
/** MYAC-1182 — the simplest shape here: a title, a filename list and the
 *  explanation of what "add" does, no extra inputs. Mirrors `isReassess` /
 *  `isStandardWatch` — a flat action check, no `kind` to switch on. */
/**
 * MYAC-1127 — a Kanban board change. ONE action value for all three shapes
 * (edit / drafted content / create), because they differ in what they propose
 * and not in how they are answered: one queue, one executor, one card.
 *
 * Free, like the register edits and for the same reason: the card is informed
 * consent — the user reads the exact before/after — not a billing gate.
 */
const isTask = computed(() => props.proposal.action === 'task_command')

/**
 * MYAC-1135 — render by what the card CARRIES, not by which tool produced it.
 *
 * The chat's nine task tools became two, so `kind` is now `change`, `lifecycle`
 * or `change+lifecycle` where it used to name a gesture. Branching on the gesture
 * was always the wrong axis — one card can now change three fields, attach a
 * document and send the task back, and there is no single `kind` that describes
 * that. Asking "are there field rows?" and "is there a lifecycle move?" describes
 * every card, old and new.
 *
 * The nine old kinds are kept below and deliberately: this ships BEFORE the
 * chatbot that emits the new ones, so for the length of that window every card
 * arriving here is still an old one. They contract once the backend has shipped.
 */
const isTaskEdit = computed(
    () => isTask.value && (props.proposal.changes?.length ?? 0) > 0)
const isTaskDocuments = computed(
    () => isTask.value && (props.proposal.document_changes?.length ?? 0) > 0)
const isTaskCreate = computed(() => isTask.value && props.proposal.kind === 'create')

/** Which lifecycle move, from either shape. The new card says so outright; the
 *  old one said it in `kind`, one kind per move. */
const taskLifecycle = computed<string>(() => {
    if (!isTask.value) return ''
    if (props.proposal.lifecycle) return props.proposal.lifecycle
    const kind = props.proposal.kind ?? ''
    return ['reject', 'archive', 'reapprove'].includes(kind) ? kind : ''
})
const isTaskReject = computed(() => taskLifecycle.value === 'reject')
const isTaskArchive = computed(() => taskLifecycle.value === 'archive')
const isTaskReapprove = computed(() => taskLifecycle.value === 'reapprove')
// MYAC-1654 — the same two commands, on an action nobody added yet: said as the
// gesture the user made rather than as a revival or a send-back.
const isRowAccept = computed(() => isTaskReapprove.value && props.proposal.lifecycle_label === 'accept')
const isRowSetAside = computed(() => isTaskReject.value && props.proposal.lifecycle_label === 'set_aside')
const setAsideReasonKey = computed(() => rejectionReasonKey(props.proposal.reason_kind))

// ── The nine-tool shape, still arriving until the chatbot side ships ─────────
const isTaskContent = computed(() => isTask.value && props.proposal.kind === 'action_content')
const isTaskAddDocument = computed(() => isTask.value && props.proposal.kind === 'add_document')
const isTaskAddAction = computed(() => isTask.value && props.proposal.kind === 'add_action')
const isTaskRemoveDocument = computed(() => isTask.value && props.proposal.kind === 'remove_document')

/**
 * A task card whose `kind` this build does not recognise.
 *
 * It MUST NOT fall through to the edit wording. That is what shipped: six new
 * backend kinds rendered as "Update <task>?" over the generic edit sentence, so
 * a card that was going to attach a document — or REMOVE one — told the user it
 * was an edit and showed nothing of what it would do.
 *
 * The agent already sends its own English `title`/`message`, so an unknown kind
 * degrades to a sentence that is at least TRUE, rather than a localised lie.
 */
const KNOWN_TASK_KINDS = [
    // MYAC-1135
    'change', 'lifecycle', 'change+lifecycle',
    // MYAC-1127 — the nine-tool kinds, still in flight during the deploy window
    'edit', 'action_content', 'create',
    'add_document', 'add_action', 'remove_document',
    'reject', 'archive', 'reapprove',
]
const isUnknownTaskKind = computed(
    () => isTask.value && !KNOWN_TASK_KINDS.includes(props.proposal.kind ?? ''))

/** One line per document change, in the user's language.
 *
 *  The op is the verb and the document is the object, so the sentence is built
 *  here rather than sent as English prose — same reason `changes` sends a
 *  `label_key` and not a label. */
const KNOWN_DOC_OPS = ['add_document', 'add_action', 'action_content', 'remove_document']

function documentChangeLabel(row: { op: string; document: string }): string {
    // Allow-listed, not derived — the same rule as `task_doc_${type}` above
    // (review #550), and for a sharper reason here: this component ships BEFORE
    // the chatbot that emits these rows, so an op this build predates is not
    // hypothetical. A key built at runtime from an unknown value resolves to
    // nothing and vue-i18n renders the KEY itself —
    // "chat.action_approval.task_doc_op.reorder" — in front of the user. An
    // unknown op falls back to naming the document, which is the half that is
    // still true.
    return KNOWN_DOC_OPS.includes(row.op)
        ? ct(`chat.action_approval.task_doc_op.${row.op}`, { document: row.document })
        : row.document
}

/** A document row's second line, which must never be a database value.
 *
 *  `detail` is free text (a step's name, its drafted content) on most rows, but
 *  an attach row arrives with the document's action type in it — `TO_UPDATE`
 *  printed as is on a live card (MYAC-1638). A known state is translated, through
 *  the same allow-list as `task_doc_${type}`; any other enum-shaped value is
 *  dropped, because the row's first line already says what happens. */
const KNOWN_DOC_STATES = ['create', 'update', 'attach']
const ENUM_SHAPED = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/

function documentChangeDetail(detail: string | undefined): string {
    const raw = (detail ?? '').trim()
    const state = /^to_([a-z]+)$/i.exec(raw)?.[1]?.toLowerCase() ?? ''
    if (KNOWN_DOC_STATES.includes(state)) return ct(`chat.action_approval.task_doc_state.${state}`)
    return ENUM_SHAPED.test(raw) ? '' : (detail ?? '')
}

/**
 * Whether this card puts MODEL-AUTHORED text in front of the user.
 *
 * The values on an edit, the drafted content, a created task's fields and the
 * actions on an added document are all the model's words. A rejection reason is
 * the USER'S, and archive / re-approve / remove show no authored content — only
 * a name that already existed.
 */
const showsAiAuthoredContent = computed(
    () => isTask.value
        && ['change', 'change+lifecycle',
            // MYAC-1127's kinds, until the deploy window closes
            'edit', 'create', 'action_content', 'add_document', 'add_action']
            .includes(props.proposal.kind ?? ''))

const taskChanges = computed(() => props.proposal.changes ?? [])
const taskDocumentChanges = computed(() => props.proposal.document_changes ?? [])
const taskActions = computed(() => props.proposal.actions ?? [])
const taskFields = computed(() => props.proposal.fields ?? [])

/**
 * A changed field's label, from the key the AGENT sent.
 *
 * The key comes out of the platform's own `EditPolicy.i18n_keys` — the same map
 * the revision trail renders through — so the card and the trail cannot disagree
 * about what a field is called. `label` is the English fallback for a field whose
 * key the policy has not registered; showing the raw dotted path would be the
 * failure this map exists to prevent.
 */
function fieldLabel(row: { label_key?: string; label?: string; field: string }): string {
    return row.label_key ? ct(row.label_key) : (row.label ?? row.field)
}

// A paid run is one that spends credits on ACCEPT — a repair or a re-assessment.
const startsAPaidRun = computed(() => isRepair.value || isReassess.value)
/** 0 requirements means the WHOLE referential, which no count can price — gate on at
 *  least one unit rather than silently charging nothing. */
const reassessCount = computed(() => {
    if (!isReassess.value) return 1
    const n = props.proposal.n_requirements ?? 0
    return n > 0 ? n : 1
})

/** The SAME key the Referentials page gates the identical scoped re-run with. */
const repairCreditAction = ASSESSMENT_CREDIT_ACTION

// Localize from the register key; fall back to the English title/message the tool sent.
const registerLabel = computed(() => {
    const key = props.proposal.register ?? ''
    const known = ['nc', 'capa', 'supplier', 'change_control']
    return known.includes(key) ? ct(`chat.action_approval.register.${key}`) : key
})

const previewColumns = computed(() => props.proposal.columns ?? [])
const previewRows = computed(() => (props.proposal.rows ?? []).slice(0, MAX_PREVIEW_ROWS))
const hiddenRowCount = computed(() =>
    Math.max(0, (props.proposal.row_count ?? props.proposal.rows?.length ?? 0) - previewRows.value.length))

const titleText = computed(() => {
    if (isRepair.value && props.proposal.requirement) {
        return ct('chat.action_approval.repair_title', { requirement: props.proposal.requirement })
    }
    if (isEdit.value && props.proposal.register) {
        const rowCount = props.proposal.row_count ?? 0
        const params = { register: registerLabel.value, count: rowCount }
        return isAppend.value
            ? ct('chat.action_approval.append_title', params, rowCount)
            : ct('chat.action_approval.delete_title', params, rowCount)
    }
    if (isTask.value) {
        const task = props.proposal.task_name ?? ''
        const document = props.proposal.document ?? ''
        if (isUnknownTaskKind.value) return props.proposal.title ?? ct('chat.action_approval.title_generic')
        if (isTaskCreate.value) return ct('chat.action_approval.task_create_title', { task })
        if (isTaskContent.value) return ct('chat.action_approval.task_content_title', { document })
        if (isTaskAddDocument.value) return ct('chat.action_approval.task_add_document_title', { document, task })
        if (isTaskAddAction.value) return ct('chat.action_approval.task_add_action_title', { document })
        if (isTaskRemoveDocument.value) return ct('chat.action_approval.task_remove_document_title', { document, task })
        if (isRowSetAside.value) return ct('chat.action_approval.task_set_aside_title', { task })
        if (isRowAccept.value) return ct('chat.action_approval.task_row_accept_title', { task })
        if (isTaskReject.value) return ct('chat.action_approval.task_reject_title', { task })
        if (isTaskArchive.value) return ct('chat.action_approval.task_archive_title', { task })
        if (isTaskReapprove.value) return ct('chat.action_approval.task_reapprove_title', { task })
        return ct('chat.action_approval.task_edit_title', { task })
    }
    return props.proposal.register
        ? ct('chat.action_approval.title', { register: registerLabel.value })
        : (props.proposal.title ?? ct('chat.action_approval.title_generic'))
})

const messageText = computed(() => {
    // Composed from i18n parts, NOT from `proposal.message`. The tool builds that string
    // in English; the conversation is often French, and rendering it verbatim put an
    // English body under a French title. `message` remains the fallback for any surface
    // without i18n of its own.
    if (isRepair.value) {
        const requirement = props.proposal.requirement ?? ''
        const file = props.proposal.source_filename ?? ''
        const rating = props.proposal.current_rating ?? ''
        const parts = [
            file
                ? ct('chat.action_approval.repair_message_file', { requirement, file })
                : ct('chat.action_approval.repair_message', { requirement }),
        ]
        if (rating) parts.push(ct('chat.action_approval.repair_current_rating', { rating }))
        parts.push(ct('chat.action_approval.repair_outcome_unknown'))
        return parts.join(' ')
    }
    if (isEdit.value && props.proposal.register) {
        if (isAppend.value) {
            return ct('chat.action_approval.append_message', { file: props.proposal.source_filename ?? '' })
        }
        return props.proposal.source_filename
            ? `${ct('chat.action_approval.delete_message')} ${ct('chat.action_approval.delete_by_file', { file: props.proposal.source_filename })}`
            : ct('chat.action_approval.delete_message')
    }
    if (isTask.value) {
        if (isUnknownTaskKind.value) return props.proposal.message ?? ''
        if (isTaskCreate.value) return ct('chat.action_approval.task_create_message')
        if (isTaskContent.value) {
            return ct('chat.action_approval.task_content_message', {
                action: props.proposal.action_label ?? '',
                task: props.proposal.task_name ?? '',
            })
        }
        if (isTaskAddDocument.value) {
            // Allow-listed, not derived (review #550). `task_doc_${type}` is a key
            // built at runtime, so a value this build predates — a fourth
            // action_type from a newer agent — resolves to nothing and vue-i18n
            // renders the KEY: "chat.action_approval.task_doc_delete", in front of
            // the user. That is the failure `chatFocusSuggestions.test.ts` exists
            // to catch, and no parity check can see it because the key is never a
            // literal. An unknown type simply drops the clause.
            const type = String(props.proposal.action_type ?? '').toLowerCase().replace('to_', '')
            const known = ['create', 'update', 'attach'].includes(type)
            return ct('chat.action_approval.task_add_document_message', {
                type: known ? ct(`chat.action_approval.task_doc_${type}`) : '',
            })
        }
        if (isTaskAddAction.value) return ct('chat.action_approval.task_add_action_message')
        if (isTaskRemoveDocument.value) return ct('chat.action_approval.task_remove_document_message')
        if (isRowSetAside.value) return ct('chat.action_approval.task_set_aside_message')
        if (isTaskReject.value) return ct('chat.action_approval.task_reject_message')
        if (isTaskArchive.value) return ct('chat.action_approval.task_archive_message')
        if (isTaskReapprove.value) {
            return ct(props.proposal.content_mode === 'reset'
                ? 'chat.action_approval.task_reapprove_reset'
                : 'chat.action_approval.task_reapprove_keep')
        }
        return ct('chat.action_approval.task_edit_message')
    }
    return props.proposal.register
        ? ct('chat.action_approval.message', { register: registerLabel.value })
        : (props.proposal.message ?? '')
})

/**
 * The confirm verb: ONE line per action the chat can raise (R16, MYAC-1670). Every
 * card names the action it takes. An action this build does not know gets the
 * neutral « Confirm », never another flow's verb: the fallback used to be the
 * register-refresh verb, so the withdraw and the PAID re-assess cards both read
 * « Refresh » on dev. A new chat action adds its line here AND a row to the VERBS
 * table in ActionProposalCard.test.ts. Nothing can detect an action the chatbot adds
 * without either (it lives in another repo); the neutral « Confirm » is what keeps
 * such a card honest until its line is added.
 * Task cards and the standards decision answer on their `kind` / `decision`.
 */
const APPROVE_KEY_BY_ACTION: Record<string, string> = {
    trigger_register_refresh: 'approve',
    propose_register_append: 'apply',
    propose_register_delete: 'apply',
    propose_requirement_repair: 'repair_approve',
    propose_reassessment: 'reassess_approve',
    // MYAC-1476: free (the page's own `adopt`, `assess: false`), no credit gate.
    propose_adopt_referential: 'adopt_approve',
    // MYAC-1476: free but irreversible; the warning keys on `destructive`, not here.
    propose_unadopt_referential: 'unadopt_approve',
    // MYAC-1477: withdraw an answer or a piece of evidence the user recorded.
    propose_playbook_withdrawal: 'withdraw_approve',
}

function taskApproveKey(): string {
    if (isTaskCreate.value) return 'task_create_approve'
    if (isTaskAddDocument.value) return 'task_add_approve'
    if (isTaskRemoveDocument.value) return 'task_remove_approve'
    if (isTaskArchive.value) return 'task_archive_approve'
    if (isRowAccept.value) return 'task_add_approve'
    if (isRowSetAside.value) return 'task_set_aside_approve'
    return 'apply'
}

const approveLabel = computed(() => {
    const action = props.proposal.action
    let key: string
    if (isTask.value) key = taskApproveKey()
    else if (action === 'propose_standard_disposition') key = isStandardWatch.value ? 'watch_approve' : 'dismiss_approve'
    else key = APPROVE_KEY_BY_ACTION[action] ?? 'confirm'
    // Resolve-and-compare (frontend rule for a key built at run time): a missing key
    // falls back to the neutral verb, never to the raw key on a customer's screen.
    const path = `chat.action_approval.${key}`
    const label = ct(path)
    return label === path ? ct('chat.action_approval.confirm') : label
})

const declineLabel = computed(() => ct('chat.action_approval.decline'))

async function decide(decision: 'approve' | 'decline') {
    if (busy.value) return
    busy.value = true
    try {
        await chatbotStore.respondToActionProposal(props.proposal, decision)
        // Only after the decision actually SENT — a throw above (the sentinel
        // message failed to go out) must not count as an approval/decline.
        // `kind` MUST come from the closed action set. The same proposal object
        // carries `requirement` and `scope_label`, free text written for chat
        // prose — neither may ever reach trackAction.
        const kind = props.proposal.action === 'task_command'
            ? props.proposal.kind
            : props.proposal.action
        try {
            trackAction(
                decision === 'approve' ? CsmAction.ChatActionApproved : CsmAction.ChatActionDeclined,
                { projectId: chatbotStore.projectId ?? undefined, projectMeta: { kind } },
            )
        } catch { }
    } finally {
        busy.value = false
    }
}
</script>
